import addInfoBarMessage from '../actions/addInfoBarMessage';
import onSmimeOptionsChange, { onSmimeOptionsDirty } from '../actions/onSmimeOptionsChange';
import ComposeViewState from '../store/schema/ComposeViewState';
import { ClientAttachmentId } from 'owa-attachment-model-store';
import { AttachmentType } from 'owa-attachment-well-data';
import updateChangeKeyOnItemId from 'owa-attachment-well-data/lib/actions/updateChangeKeyOnItemId';
import deleteAttachment from 'owa-attachment-well-data/lib/services/deleteAttachment';
import { onDeleteSmimeAttachment } from 'owa-mail-store-actions';
import { getStore } from 'owa-mail-store/lib/store/Store';
import { ItemId } from 'owa-preview-pane-store';
import DeleteAttachmentResponseMessage from 'owa-service/lib/contract/DeleteAttachmentResponseMessage';
import { lazySmimeAdapter } from 'owa-smime';
import SmimeViewState from 'owa-smime/lib/store/schema/SmimeViewState';
import smimeStore from 'owa-smime/lib/store/store';
import getSmimeAttachmentType from 'owa-smime/lib/utils/getSmimeAttachmentType';
import getSmimeInitializationErrorInfoBar from 'owa-smime/lib/utils/getSmimeInitializationErrorInfoBar';
import isSmimeAdapterUsable from 'owa-smime/lib/utils/isSmimeAdapterUsable';
import { isInitialized } from 'owa-smime/lib/utils/smimeUtils';
import { trace } from 'owa-trace';
import { orchestrator } from 'satcheljs';

/**
 * Orchestrator that fetches the S/MIME adapter if S/MIME options are set
 */
export default orchestrator(onSmimeOptionsChange, async actionMessage => {
    const { viewState, shouldEncryptMessageAsSmime, shouldSignMessageAsSmime } = actionMessage;
    const { smimeViewState } = viewState;
    // Do not perform any action if the viewState is same
    if (
        !isSmimeViewStateDirty(
            smimeViewState,
            shouldEncryptMessageAsSmime,
            shouldSignMessageAsSmime
        )
    ) {
        return;
    }
    if (!shouldEncryptMessageAsSmime && !shouldSignMessageAsSmime) {
        // S/MIME to non-S/MIME scenario
        onSmimeModeDisabled(viewState);
    } else if (
        !smimeViewState.shouldEncryptMessageAsSmime &&
        !smimeViewState.shouldSignMessageAsSmime
    ) {
        // Non-S/MIME to S/MIME scenario
        onSmimeModeEnabled(viewState);
    }
    onSmimeOptionsDirty(viewState, shouldEncryptMessageAsSmime, shouldSignMessageAsSmime);
    const isSmimeMessage = shouldEncryptMessageAsSmime || shouldSignMessageAsSmime;
    if (isSmimeMessage && !isInitialized(smimeStore.installationStatus)) {
        await lazySmimeAdapter.import();
        if (!isSmimeAdapterUsable()) {
            addInfoBarMessage(
                viewState,
                'loadControlNotInstalledInfobar',
                getSmimeInitializationErrorInfoBar
            );
        }
    }
});

/**
 * This util will return
 * True - if the smimeViewState is dirty, False - otherwise
 */
function isSmimeViewStateDirty(
    smimeViewState: SmimeViewState,
    isMessageEncrypted: boolean,
    isMessageSigned: boolean
): boolean {
    if (smimeViewState) {
        const { shouldEncryptMessageAsSmime, shouldSignMessageAsSmime } = smimeViewState;
        return (
            shouldEncryptMessageAsSmime != isMessageEncrypted ||
            shouldSignMessageAsSmime != isMessageSigned
        );
    }
    return false;
}

function onSmimeModeEnabled(viewState: ComposeViewState) {
    // TODO WI 43448: Handle toggle scenario
}

function onSmimeModeDisabled(viewState: ComposeViewState) {
    // TODO WI 43448: Handle toggle scenario
    if (viewState.itemId) {
        // Check and remove p7m file if its a switch from S/MIME message to non-S/MIME message
        // and item is already created
        tryDeleteSmimeAttachmentType(viewState.itemId);
    }
}

// Delete the S/MIME attachment type (p7m file), if present in the item
async function tryDeleteSmimeAttachmentType(itemId: ItemId) {
    const item = getStore().items.get(itemId.Id);
    const smimeAttachment: AttachmentType = getSmimeAttachmentType(item);

    if (item && smimeAttachment && smimeAttachment.AttachmentId) {
        try {
            const attachmentId: ClientAttachmentId = {
                Id: smimeAttachment.AttachmentId.Id,
                mailboxInfo: item.MailboxInfo,
            };
            const response = await deleteAttachment(attachmentId);
            const responseMessage: DeleteAttachmentResponseMessage =
                response.ResponseMessages.Items[0];
            if (responseMessage.ResponseClass === 'Success') {
                updateChangeKeyOnItemId(itemId, responseMessage.RootItemId.RootItemChangeKey);
                // Update item in the store
                onDeleteSmimeAttachment(itemId.Id);
            } else {
                trace.warn(
                    `Smime Attachment: Failed to delete attachment with ResponseClass= ${
                        responseMessage.ResponseClass
                    }, ResponseCode=${responseMessage.ResponseCode}`
                );
            }
        } catch (error) {
            trace.warn('Smime Attachment: deleteAttachment got error: ' + error.message);
        }
    }
}
